/** GxTakeover: the full-screen sheet over a page (a live surprise, a halted solo year). It opens at its top, whatever the page behind it was scrolled to. */
import { Children, isValidElement, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useFocusTrap } from "./focusTrap";

const DISMISS_EVENT = "gx-dismiss-takeovers";

/**
 * Puts away every takeover on screen now (the Results page calls it once the round's result is released: the year has run, so a surprise that paused it is over). A takeover that opens later,
 * for another card, is not affected: each remembers the cards it was showing when it was put away.
 */
export const dismissTakeovers = (): void => { window.dispatchEvent(new Event(DISMISS_EVENT)); };

/** What a takeover is showing: the keys of its children (the card is keyed by its instance), so a different card is a different sheet. */
const shownKey = (children: ReactNode): string => Children.toArray(children).map((c) => (isValidElement(c) ? String(c.key) : "")).join("|");

type Props = { readonly label: string; readonly onClose?: () => void; readonly testId: string; readonly className?: string; readonly children: ReactNode };

function Sheet({ label, testId, className, onClose, children }: Props) {
  const sheet = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => { sheet.current?.scrollTo?.(0, 0); if (window.scrollY !== 0) window.scrollTo(0, 0); }, []);
  const onKeyDown = useFocusTrap(sheet, onClose);
  return (
    <div ref={sheet} className={className === undefined ? "gx-takeover" : `gx-takeover ${className}`} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} onKeyDown={onKeyDown} data-testid={testId}>
      <div className="gx-takeover-in">{children}</div>
    </div>
  );
}

export function GxTakeover(props: Props) {
  const key = shownKey(props.children);
  const now = useRef(key);
  now.current = key;
  const [away, setAway] = useState<string | null>(null);
  useEffect(() => {
    const put = (): void => setAway(now.current);
    window.addEventListener(DISMISS_EVENT, put);
    return () => window.removeEventListener(DISMISS_EVENT, put);
  }, []);
  return away === key ? null : <Sheet {...props} />;
}
